/**
 * Solfeggio tone sessions — the same generator the Android app ships
 * (android/app/…/Solfeggio.kt), built on demand through the SAME schema
 * types as every other preset. No second audio path.
 *
 * Technique choice is deliberate: a binaural/monaural beat would move the
 * energy to sidebands (f ± beat/2) — the tone would no longer BE 528 Hz.
 * Pure mode is a monaural layer with beat 0 (exactly f); pulse mode is an
 * isochronic gate (carrier stays exactly f, gated at 6 Hz). Both are
 * speaker-safe by construction.
 *
 * Ids are UUIDv3 over the same name string as the Kotlin generator (Java's
 * UUID.nameUUIDFromBytes = MD5 + version/variant bits), so the same
 * tone+mode+duration is the SAME preset on both platforms — cloud sync
 * sees one document, not two. Keep the recipe in lockstep with the Kotlin
 * side; the cross-platform id pin lives in both test suites.
 *
 * Positioning is honest (non-negotiable): labels are traditional
 * associations, stated as tradition — never as healing claims.
 */

import type { Layer, Preset, Segment } from "../../schema/types.ts";

export interface SolfeggioTone {
  freqHz: number;
  label: string;
}

/** The nine traditional tones with their conventional one-word themes. */
export const SOLFEGGIO_TONES: SolfeggioTone[] = [
  { freqHz: 174, label: "Ease" },
  { freqHz: 285, label: "Restore" },
  { freqHz: 396, label: "Release" },
  { freqHz: 417, label: "Change" },
  { freqHz: 528, label: "Love" },
  { freqHz: 639, label: "Connect" },
  { freqHz: 741, label: "Express" },
  { freqHz: 852, label: "Intuition" },
  { freqHz: 963, label: "Awaken" },
];

export const SOLFEGGIO_DURATIONS_MIN = [10, 20, 45];

// ---------------------------------------------------------------- stable id

/**
 * MD5 (RFC 1321) over raw bytes. Written out here because WebCrypto has no
 * MD5 and the id contract requires Java's UUID.nameUUIDFromBytes exactly;
 * verified against node:crypto in solfeggio.test.ts.
 */
function md5(input: Uint8Array): Uint8Array {
  const S = [
    7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
    5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
    4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
    6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
  ];
  const K = new Uint32Array(64);
  for (let i = 0; i < 64; i++) K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296);

  const padded = new Uint8Array((((input.length + 8) >> 6) + 1) << 6);
  padded.set(input);
  padded[input.length] = 0x80;
  const view = new DataView(padded.buffer);
  const bitLen = input.length * 8;
  view.setUint32(padded.length - 8, bitLen >>> 0, true);
  view.setUint32(padded.length - 4, Math.floor(bitLen / 4294967296), true);

  let a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;
  const M = new Uint32Array(16);
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) M[i] = view.getUint32(off + i * 4, true);
    let a = a0, b = b0, c = c0, d = d0;
    for (let i = 0; i < 64; i++) {
      let f: number, g: number;
      if (i < 16) { f = (b & c) | (~b & d); g = i; }
      else if (i < 32) { f = (d & b) | (~d & c); g = (5 * i + 1) % 16; }
      else if (i < 48) { f = b ^ c ^ d; g = (3 * i + 5) % 16; }
      else { f = c ^ (b | ~d); g = (7 * i) % 16; }
      const rot = S[i]!;
      const sum = (a + f + K[i]! + M[g]!) >>> 0;
      const nb = (b + (((sum << rot) | (sum >>> (32 - rot))) >>> 0)) >>> 0;
      a = d; d = c; c = b; b = nb;
    }
    a0 = (a0 + a) >>> 0; b0 = (b0 + b) >>> 0; c0 = (c0 + c) >>> 0; d0 = (d0 + d) >>> 0;
  }
  const out = new Uint8Array(16);
  const ov = new DataView(out.buffer);
  ov.setUint32(0, a0, true);
  ov.setUint32(4, b0, true);
  ov.setUint32(8, c0, true);
  ov.setUint32(12, d0, true);
  return out;
}

/** UUIDv3 over a name, byte-identical to Java's UUID.nameUUIDFromBytes. */
export function nameUuidV3(name: string): string {
  const md = md5(new TextEncoder().encode(name));
  md[6] = (md[6]! & 0x0f) | 0x30;
  md[8] = (md[8]! & 0x3f) | 0x80;
  const hex = Array.from(md, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ------------------------------------------------------------------ preset

export function buildSolfeggioPreset(
  freqHz: number,
  label: string,
  pure: boolean,
  durationMin: number,
): Preset {
  const dur = durationMin * 60;
  const mode = pure ? "pure" : "pulse";
  const segments: Segment[] = [
    {
      t0: 0, t1: dur,
      carrierHz: [freqHz, freqHz],
      beatHz: pure ? [0, 0] : [6, 6],
      curve: "hold",
    },
  ];
  const tone: Layer = pure
    ? { id: "tone", type: "monaural", gainDb: -10, segments }
    : { id: "tone", type: "isochronic", gainDb: -10, dutyCycle: 0.5, gateShape: "raisedCosine", segments };
  return {
    schema: "kalmtide/1",
    meta: {
      id: nameUuidV3(`kalmtide-solfeggio-${freqHz}-${mode}-${durationMin}`),
      name: `${freqHz} Hz · ${label}`,
      goal: "meditate",
      author: "kalmtide",
      durationSec: dur,
    },
    allowFallback: true,
    layers: [
      tone,
      { id: "bed", type: "noise", color: "brown", gainDb: -34 },
    ],
    master: { gainDb: 0, fadeInSec: 3, fadeOutSec: 10, limiter: true },
  };
}

// ------------------------------------------------------------------ dialog

/** Modal tone picker mirroring the app's SolfeggioScreen. */
export function openSolfeggioDialog(onCreate: (preset: Preset) => void): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  const box = document.createElement("div");
  box.className = "modal-box solf-box";
  box.innerHTML = `
    <h3>New Solfeggio session</h3>
    <p class="solf-sub">Nine tones from a centuries-old scale. The names are the
      traditional associations — tradition, not medicine.</p>
    <div class="solf-chips" data-role="mode">
      <button class="solf-chip selected" data-pure="0">6 Hz pulse</button>
      <button class="solf-chip" data-pure="1">Pure tone</button>
    </div>
    <div class="solf-chips" data-role="duration">
      ${SOLFEGGIO_DURATIONS_MIN.map(
        (d) => `<button class="solf-chip${d === 20 ? " selected" : ""}" data-min="${d}">${d} min</button>`,
      ).join("")}
    </div>
    <p class="solf-hint"></p>
    <div class="solf-grid">
      ${SOLFEGGIO_TONES.map(
        (t) =>
          `<button class="solf-tone" data-freq="${t.freqHz}">` +
          `<span class="freq">${t.freqHz}</span><span class="unit">Hz</span>` +
          `<span class="label">${t.label}</span></button>`,
      ).join("")}
    </div>
  `;
  overlay.append(box);

  let pure = false;
  let durationMin = 20;
  const hint = box.querySelector<HTMLElement>(".solf-hint")!;
  const paintHint = (): void => {
    hint.textContent = pure
      ? "The unmodified frequency, continuously."
      : "The tone stays exactly on frequency, gently gated at a 6 Hz theta rhythm.";
  };
  paintHint();

  const selectIn = (role: string, target: HTMLElement): void => {
    box.querySelectorAll(`[data-role="${role}"] .solf-chip`).forEach((el) => el.classList.remove("selected"));
    target.classList.add("selected");
  };
  box.querySelectorAll<HTMLElement>('[data-role="mode"] .solf-chip').forEach((el) =>
    el.addEventListener("click", () => {
      pure = el.dataset.pure === "1";
      selectIn("mode", el);
      paintHint();
    }),
  );
  box.querySelectorAll<HTMLElement>('[data-role="duration"] .solf-chip').forEach((el) =>
    el.addEventListener("click", () => {
      durationMin = Number(el.dataset.min);
      selectIn("duration", el);
    }),
  );

  const close = (): void => {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  box.querySelectorAll<HTMLElement>(".solf-tone").forEach((el) =>
    el.addEventListener("click", () => {
      const tone = SOLFEGGIO_TONES.find((t) => t.freqHz === Number(el.dataset.freq))!;
      close();
      onCreate(buildSolfeggioPreset(tone.freqHz, tone.label, pure, durationMin));
    }),
  );

  document.body.append(overlay);
}
